/**
 * Peer nomination — PRF-13. One flat router, `peerNominationContract` (`/peer-nominations`).
 *
 * "Employees nominate peers (min/max set per cycle), manager approves/edits before requests go
 * out" (the plan's own wording, verbatim). This is deliberately NOT `@hrms/workflow` (CLAUDE.md
 * invariant 6): the nominee's own manager is a single flat gate, not a multi-step approval chain —
 * the same "direct, permission-gated action" shape `calibration.ts`'s `submitAdjustment` uses, not
 * `finalizeSession`'s workflow-backed one.
 *
 * EDITING IS FOLDED INTO DECIDING. `decideNomination`'s optional `nomineePersonIds` REPLACES the
 * nominee list before approving/rejecting, rather than a separate edit-then-decide round trip —
 * one action, not two. Only `submitNomination`'s ORIGINAL list is ever employee-authored; every
 * subsequent state is the manager's call.
 *
 * NOTHING HERE SENDS A FeedbackRequest. Approving a nomination is what creates the real
 * `FeedbackRequest` rows (`relationship: 'peer'`) — `routes/peer-nomination.ts` reuses the exact
 * same creation shape `feedback.ts`'s `createRequests` already established, never a second,
 * divergent implementation of "what a feedback request looks like".
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const peerNominationStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type PeerNominationStatus = z.infer<typeof peerNominationStatusSchema>;

export const peerNomineeSchema = z.object({
  id: uuidSchema,
  nomineePersonId: uuidSchema,
  nomineeName: z.string(),
  /** Set only once the nomination is approved and a real FeedbackRequest exists for this nominee. */
  feedbackRequestId: uuidSchema.nullable(),
});
export type PeerNominee = z.infer<typeof peerNomineeSchema>;

export const peerNominationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  cycleId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  managerPersonId: uuidSchema.nullable(),
  status: peerNominationStatusSchema,
  managerComment: z.string().nullable(),
  decidedByPersonId: uuidSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  nominees: z.array(peerNomineeSchema),
  createdAt: instantSchema,
});
export type PeerNomination = z.infer<typeof peerNominationSchema>;

export const listNominationsQuerySchema = paginationQuerySchema.extend({
  cycleId: uuidSchema.optional(),
  status: peerNominationStatusSchema.optional(),
});

/** Validated against the cycle's own `peerNominationMinCount`/`peerNominationMaxCount` server-side
 * — the array bound here (1-20) is only the outer shape limit, same convention
 * `feedback.contract.ts`'s `createRequestsBodySchema.respondents` uses. */
export const submitNominationBodySchema = z.object({
  cycleId: uuidSchema,
  nomineePersonIds: z.array(uuidSchema).min(1).max(20),
});

export const decideNominationBodySchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  /** Replaces the nominee list before deciding (the plan's "edits" — folded into this one call).
   * Omitted keeps the employee's own submitted list as-is. */
  nomineePersonIds: z.array(uuidSchema).max(20).optional(),
  comment: z.string().trim().max(2000).optional(),
});

export const peerNominationContract = c.router(
  {
    listNominations: {
      method: 'GET',
      path: '/',
      query: listNominationsQuerySchema,
      responses: {
        200: paginatedSchema(peerNominationSchema),
        401: errorBodySchema,
      },
      summary:
        "List peer nominations in the caller's own scope — their own submitted ones, or (for a manager) their direct reports'",
      metadata: routeMeta(null),
    },

    getNomination: {
      method: 'GET',
      path: '/:nominationId',
      responses: {
        200: peerNominationSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one peer nomination',
      metadata: routeMeta(null),
    },

    submitNomination: {
      method: 'POST',
      path: '/',
      body: submitNominationBodySchema,
      responses: {
        201: peerNominationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "An employee nominates their own peer reviewers for a cycle — validated against the cycle's peerNominationMinCount/MaxCount",
      metadata: routeMeta('feedback.request:create'),
    },

    decideNomination: {
      method: 'POST',
      path: '/:nominationId/decide',
      body: decideNominationBodySchema,
      responses: {
        200: peerNominationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "The nominee's manager approves (optionally editing the list first) or rejects — approving creates a real FeedbackRequest per surviving nominee",
      metadata: routeMeta('feedback.request:create'),
    },
  },
  { pathPrefix: '/peer-nominations' },
);
